import { useMemo, useState } from 'react';

import {
  ActionDialogButtons,
  ActionDialogContent,
  BarChart,
  BarChartSkeleton,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTrigger,
  IconButton,
  LineChart,
  LineChartSkeleton,
  SelectDropdown,
} from '@opositandopn/ui';
import {
  IoAnalyticsOutline,
  IoClipboardOutline,
  IoFileTrayFullOutline,
  IoStatsChartOutline,
  IoTimerOutline,
  IoTrashOutline,
} from 'react-icons/io5';

import { Page } from '../../components/shared/page/Page';
import { ValueCard } from '../../components/shared/value-card/ValueCard';
import {
  APTITUDE_SUBJECT_ID,
  ENGLISH_SUBJECT_ID,
  THEORY_SUBJECT_ID,
} from '../../constants/subjects';
import { useProfile } from '../../context/ProfileContext';
import {
  useGetUserMetrics,
  useResetUserMetrics,
} from '../../features/users/api/useUsers';
import { formatSortDayDate } from '../../utils/date';

import styles from './StatisticsPage.module.scss';

function StatisticsPage() {
  const { profile } = useProfile();

  const [selectedSubject, setSelectedSubject] =
    useState<string>(THEORY_SUBJECT_ID);

  const [open, setOpen] = useState(false);

  const { data: response, isLoading } = useGetUserMetrics(
    {
      userId: profile!.id!,
      subjectId: selectedSubject,
    },
    !!profile?.id,
  );

  const { mutate, isPending } = useResetUserMetrics();

  const metrics = useMemo(
    () =>
      [...(response?.metrics ?? [])].sort(
        (a, b) => a.submittedAt.getTime() - b.submittedAt.getTime(),
      ),
    [response],
  );

  return (
    <Page title="Progreso">
      <SelectDropdown
        className={styles['dropdown']}
        onSelect={(value: string | number) =>
          setSelectedSubject(value as string)
        }
        options={[
          {
            value: THEORY_SUBJECT_ID,
            label: 'Teoría',
            selected: selectedSubject === THEORY_SUBJECT_ID,
          },
          {
            value: APTITUDE_SUBJECT_ID,
            label: 'Psicotécnicos',
            selected: selectedSubject === APTITUDE_SUBJECT_ID,
          },
          {
            value: ENGLISH_SUBJECT_ID,
            label: 'Inglés',
            selected: selectedSubject === ENGLISH_SUBJECT_ID,
          },
        ]}
      />
      <div className={styles['row']}>
        <ValueCard
          className={styles['value-card']}
          icon={<IoFileTrayFullOutline />}
          title="Total simulacros"
          tooltip="Simulacros totales"
          value={response?.total ?? 0}
        />
        <ValueCard
          className={styles['value-card']}
          icon={<IoClipboardOutline />}
          title="Nota media"
          tooltip="Últimos 20 simulacros"
          value={response?.averageScore ?? 0}
          trending={
            response?.trend && response.trend > 0
              ? 'up'
              : response?.trend && response.trend < 0
                ? 'down'
                : undefined
          }
        />
        <ValueCard
          className={styles['value-card']}
          icon={<IoTimerOutline />}
          title="Velocidad por pregunta"
          tooltip="Últimos 20 simulacros"
          value={response?.questionAverageTime ?? 0}
          format={{
            style: 'unit',
            unit: 'second',
            unitDisplay: 'short',
            maximumFractionDigits: 2,
          }}
        />
      </div>
      <div className={styles['row']}>
        <Card
          className={styles['chart-card']}
          icon={<IoAnalyticsOutline />}
          title="Resultados en simulacros"
          tooltip="Últimos 20 simulacros"
        >
          {isLoading ? (
            <LineChartSkeleton labels={1} />
          ) : (
            <LineChart
              className={styles['chart']}
              label="Resultados en simulacros"
              labels={metrics.map((metric) =>
                formatSortDayDate(new Date(metric.submittedAt)),
              )}
              dataset={[
                {
                  label: 'Nota',
                  values: metrics.map((result) => result.grade),
                },
              ]}
            />
          )}
        </Card>
      </div>
      <div className={styles['row']}>
        <Card
          className={styles['chart-card']}
          icon={<IoStatsChartOutline />}
          title="Respuestas en simulacros"
          tooltip="Últimos 20 simulacros"
        >
          {isLoading ? (
            <BarChartSkeleton labels={2} />
          ) : (
            <BarChart
              className={styles['chart']}
              label="Respuestas en simulacros"
              labels={metrics.map((metric) =>
                formatSortDayDate(new Date(metric.submittedAt)),
              )}
              dataset={[
                {
                  label: 'Correctas',
                  values: metrics.map((result) => result.correctAnswers),
                  color: '#50b06d',
                },
                {
                  label: 'Erróneas',
                  values: metrics.map((result) => result.incorrectAnswers),
                  color: '#e86767',
                },
                {
                  label: 'Sin contestar',
                  values: metrics.map(
                    (result) =>
                      result.totalQuestions -
                      (result.correctAnswers + result.incorrectAnswers),
                  ),
                  color: '#a8d8e9',
                },
              ]}
              stacked
            />
          )}
        </Card>
      </div>
      {!isLoading && response && response?.total > 0 && (
        <div className={styles['attempt']}>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild={true}>
              <IconButton
                icon={<IoTrashOutline />}
                className={styles['trash']}
                onClick={() => setOpen((v) => !v)}
                disabled={isPending}
              />
            </DialogTrigger>
            <DialogContent>
              <ActionDialogContent
                title="¿Quieres reiniciar tus estadísticas y progreso?"
                subtitle="Solo se eliminarán los datos de la asignatura seleccionada; tu historial de simulacros seguirá intacto"
              >
                <ActionDialogButtons>
                  <Button
                    variant="outline-primary"
                    onClick={() => setOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      mutate(
                        {
                          userId: profile!.id!,
                          subjectId: selectedSubject,
                        },
                        {
                          onSuccess: () => {
                            setOpen(false);
                          },
                        },
                      );
                    }}
                  >
                    Eliminar datos
                  </Button>
                </ActionDialogButtons>
              </ActionDialogContent>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </Page>
  );
}

export { StatisticsPage };
